import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api'; // Ensure BASE_URL is defined in your config/api.js
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf'; // Import jsPDF
import 'jspdf-autotable'; // Import jsPDF autotable plugin

const RodiumsList = () => {
    const [rodiums, setRodiums] = useState([]);
    const [filteredRodiums, setFilteredRodiums] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const navigate = useNavigate();

    // Fetch rodium entries when the component mounts
    useEffect(() => {
        fetch(`${BASE_URL}rodiums`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setRodiums(data.data);
                    setFilteredRodiums(data.data); // Initially set filteredRodiums to all Rodium data
                } else {
                    console.error('Error fetching rodiums:', data.message);
                }
            })
            .catch(error => console.error('Error fetching rodiums:', error));
    }, []);

    // Handle search term change
    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        filterRodiums(term, startDate, endDate); // Filter based on search term and date range
    };

    // Filter rodiums based on search term, start date, and end date
    const filterRodiums = (term, start, end) => {
        let filtered = rodiums.filter(rodium => {
            const matchesSearch = rodium.customer_name.toLowerCase().includes(term.toLowerCase());

            // Convert created_at to YYYY-MM-DD format to avoid time parts interfering
            const rodiumDate = new Date(rodium.created_at).toISOString().split('T')[0]; // Format as YYYY-MM-DD

            const matchesDateRange = (
                (!start || new Date(rodiumDate) >= new Date(start)) && 
                (!end || new Date(rodiumDate) <= new Date(end))
            );

            return matchesSearch && matchesDateRange;
        });

        setFilteredRodiums(filtered); // Update filtered rodiums state
    };

    // Handle date range change (apply filters)
    const handleDateChange = () => {
        filterRodiums(searchTerm, startDate, endDate); // Reapply filters when dates change
    };

    // Navigate to the edit rodium page
    const handleEdit = (id) => {
        navigate(`/EditRodiums/${id}`);
    };

    // Handle rodium deletion
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this rodium?")) {
            fetch(`${BASE_URL}rodiums/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(response => response.json())
                .then(data => {
                    if (data.status === 1) {
                        // Remove the deleted rodium from both lists
                        setRodiums(rodiums.filter(rodium => rodium.id !== id));
                        setFilteredRodiums(filteredRodiums.filter(rodium => rodium.id !== id));
                    }
                })
                .catch(error => console.error('Error deleting rodium:', error));
        }
    };

    // Calculate total PCS (if needed for the export)
    const calculateTotalPCS = () => {
        return filteredRodiums.reduce((total, rodium) => total + (rodium.pcs || 0), 0);
    };

    const totalPCS = calculateTotalPCS();

    // Export to PDF function
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Add Title
        doc.text('Rodiums List', 14, 10); // Title at the top of the PDF

        // Add Total PCS
        doc.text(`Total PCS: ${totalPCS}`, 14, 20);

        // Prepare table data for PDF
        const tableData = filteredRodiums.map(rodium => [
            rodium.customer_name || 'N/A',
            rodium.pcs || 'N/A',
            rodium.description || 'N/A',
        ]);

        // Add table to PDF with autoTable
        doc.autoTable({
            head: [['Customer Name', 'PCS', 'Description']], // Table headers
            body: tableData, // Table body (filtered Rodiums)
        });

        // Save the PDF with a filename
        doc.save('rodiums_list.pdf');
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Rodiums List</h3>
                        </div>
                    </div>
                </div>
            </div>

            <section className="section">
                <div className="row" id="basic-table">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-md-6">
                                        <h5>Total PCS: {totalPCS}</h5>
                                    </div>
                                    <div className="col-md-6 text-end">
                                        <button className="btn btn-primary" onClick={() => navigate('/AddRodiums')}>Add Rodium</button>
                                        <button className="btn btn-secondary ms-2" onClick={handleExportPDF}>Export to PDF</button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                {/* Search and Date Range Filter */}
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label htmlFor="searchTerm" className="form-label">Search by Customer Name</label>
                                        <input
                                            type="text"
                                            id="searchTerm"
                                            className="form-control"
                                            placeholder="Search by customer name"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="startDate" className="form-label">Start Date</label>
                                        <input
                                            type="date"
                                            id="startDate"
                                            className="form-control"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="endDate" className="form-label">End Date</label>
                                        <input
                                            type="date"
                                            id="endDate"
                                            className="form-control"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <button className="btn btn-secondary" onClick={handleDateChange}>Apply Date Filter</button>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Staff ID</th>
                                                <th>Customer Name</th>
                                                <th>PCS</th>
                                                <th>Description</th>
                                                <th className="text-end">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredRodiums.map(rodium => (
                                                <tr key={rodium.id}>
                                                    <td>{rodium.id}</td>
                                                    <td>{rodium.staff_id}</td>
                                                    <td>{rodium.customer_name}</td>
                                                    <td>{rodium.pcs}</td>
                                                    <td>{rodium.description || 'N/A'}</td>
                                                    <td className="text-end">
                                                        <button className="btn btn-success" onClick={() => handleEdit(rodium.id)}>Edit</button>
                                                        <button className="btn btn-danger ms-2" onClick={() => handleDelete(rodium.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default RodiumsList;
